import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Mail, X, Sparkles, Send, Copy } from 'lucide-react';
import { toast } from 'sonner';

const EmailWriterPanel = ({
  language,
  lead,
  showEmailWriter,
  setShowEmailWriter,
  emailType,
  setEmailType,
  customEmailContext,
  setCustomEmailContext,
  emailGenerating,
  generatedEmail,
  setGeneratedEmail,
  handleGenerateEmail,
  handleSendGeneratedEmail
}) => {
  if (!showEmailWriter) return null;

  const emailTypes = [
    { value: 'follow_up', label: language === 'es' ? 'Seguimiento' : 'Follow-up' },
    { value: 'proposal_sent', label: language === 'es' ? 'Propuesta Enviada' : 'Proposal Sent' },
    { value: 'quote_reminder', label: language === 'es' ? 'Recordatorio' : 'Quote Reminder' },
    { value: 'thank_you', label: language === 'es' ? 'Agradecimiento' : 'Thank You' },
    { value: 'job_complete', label: language === 'es' ? 'Trabajo Completado' : 'Job Complete' },
    { value: 'custom', label: language === 'es' ? 'Personalizado' : 'Custom' }
  ];

  return (
    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-green-800 flex items-center">
          <Mail className="h-5 w-5 mr-2" />
          {language === 'es' ? 'Escritor de Emails AI' : 'AI Email Writer'}
        </h4>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={() => { setShowEmailWriter(false); setGeneratedEmail(null); }}
          className="text-gray-500 h-6 w-6 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          <Label className="text-sm text-green-700 w-full">
            {language === 'es' ? 'Tipo de Email:' : 'Email Type:'}
          </Label>
          {emailTypes.map(type => (
            <Button
              key={type.value}
              size="sm"
              variant={emailType === type.value ? 'default' : 'outline'}
              className={emailType === type.value ? 'bg-green-600' : ''}
              onClick={() => setEmailType(type.value)}
            >
              {type.label}
            </Button>
          ))}
        </div>
        
        {emailType === 'custom' && (
          <div>
            <Label className="text-sm text-green-700">
              {language === 'es' ? 'Describe el email:' : 'Describe the email:'}
            </Label>
            <Textarea
              value={customEmailContext}
              onChange={(e) => setCustomEmailContext(e.target.value)}
              placeholder={language === 'es' 
                ? 'Ej: Escribe un email preguntando si tienen preguntas sobre la propuesta...' 
                : 'E.g., Write an email asking if they have questions about the proposal...'}
              className="mt-1"
              rows={2}
            />
          </div>
        )}
        
        <Button 
          onClick={handleGenerateEmail}
          disabled={emailGenerating || (emailType === 'custom' && !customEmailContext)}
          className="bg-green-600 hover:bg-green-700"
        >
          <Sparkles className="h-4 w-4 mr-1" />
          {emailGenerating 
            ? (language === 'es' ? 'Generando...' : 'Generating...') 
            : (language === 'es' ? 'Generar Email' : 'Generate Email')}
        </Button>
        
        {/* Generated Email Preview */}
        {generatedEmail && (
          <div className="mt-4 p-3 bg-white rounded border">
            <div className="mb-2">
              <Label className="text-sm text-gray-500">
                {language === 'es' ? 'Asunto:' : 'Subject:'}
              </Label>
              <Input 
                value={generatedEmail.subject} 
                onChange={(e) => setGeneratedEmail({...generatedEmail, subject: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm text-gray-500">
                {language === 'es' ? 'Mensaje:' : 'Message:'}
              </Label>
              <Textarea
                value={generatedEmail.body}
                onChange={(e) => setGeneratedEmail({...generatedEmail, body: e.target.value})}
                className="mt-1"
                rows={6}
              />
            </div>
            <div className="flex gap-2 mt-3">
              <Button 
                onClick={handleSendGeneratedEmail}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!lead.email}
              >
                <Send className="h-4 w-4 mr-1" />
                {language === 'es' ? 'Enviar a ' : 'Send to '}
                {lead.email || (language === 'es' ? '(sin email)' : '(no email)')}
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(`Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`);
                  toast.success(language === 'es' ? 'Copiado!' : 'Copied!');
                }}
              >
                <Copy className="h-4 w-4 mr-1" />
                {language === 'es' ? 'Copiar' : 'Copy'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailWriterPanel;
